/**
 * EmailVerifyBanner — banner-center source (2026-08-14 owner directive).
 *
 * Any signed-in account with an unverified email sees a pressing reminder to
 * verify — unverified accounts may be paused. Dismissal is DAILY: the X hides
 * the banner only until the next 5:00 AM local, so it returns every morning
 * until the address is verified (same mechanism as the trial banner).
 *
 * Eligibility re-checks on auth state and after the user returns from their
 * inbox (visibilitychange → user.reload()), so the banner disappears the
 * moment verification lands without a full page refresh.
 *
 * The resend action goes through requestBrandedVerificationEmail (the shared
 * client entry point for verification emails — email backoff plan); the raw
 * sendEmailVerification lives only inside that helper as its fallback.
 */

import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { requestBrandedVerificationEmail } from '../../lib/identity/signInOrLink';
import { useBannerSurface } from './bannerSurface';
import { BannerBusContext, useRegisterBanner, BannerDescriptor } from './GlobalBannerCenter';
import type { BannerNotice } from './bannerNotice';
import {
  isDismissalActive,
  readDailyDismissal,
  writeDailyDismissal,
} from './bannerDismissal';

const DISMISS_KEY = 'prest0n_emailverify_dismissed';

const ResendButton = styled.button`
  flex: none;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: #8a6b2a;
  border: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
  &:disabled { opacity: 0.7; cursor: default; }
`;

export const EmailVerifyBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  const onAppSurface = useBannerSurface();

  const [user, setUser] = useState<User | null>(null);
  const [verified, setVerified] = useState<boolean>(true);
  const [dismissedAt, setDismissedAt] = useState<string | null>(() => readDailyDismissal(DISMISS_KEY));
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setVerified(u ? u.emailVerified : true);
    });
    return unsub;
  }, []);

  // Re-check when the tab regains focus — the user likely just clicked the
  // verification link in their inbox.
  useEffect(() => {
    const onVisible = () => {
      const u = getAuth().currentUser;
      if (!u || u.emailVerified) return;
      u.reload()
        .then(() => setVerified(getAuth().currentUser?.emailVerified ?? true))
        .catch(() => { /* transient — keep current state */ });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const eligible =
    !!user &&
    !!user.email &&
    !verified &&
    onAppSurface &&
    !isDismissalActive(dismissedAt);

  const handleResend = () => {
    const u = getAuth().currentUser;
    if (!u || resendState !== 'idle') return;
    setResendState('sending');
    // Never rejects (branded callable with the raw client send as its
    // internal fallback) — the catch is belt-and-braces for the state machine.
    requestBrandedVerificationEmail(u)
      .then(() => setResendState('sent'))
      .catch(() => setResendState('idle'));
  };

  const handleDismiss = () => {
    writeDailyDismissal(DISMISS_KEY);
    setDismissedAt(readDailyDismissal(DISMISS_KEY));
  };

  // The words, one place for both designs' bands (./bannerNotice.ts). The phone
  // line (2026-09-13): the address is what makes this actionable — you check
  // THAT inbox — so it survives; the consequence is one tap away.
  const email = user?.email ?? '';
  const message = `verify your email — we sent a link to ${email}. unverified accounts may be paused.`;
  const shortMessage = `verify your email — check ${email}.`;
  const resendLabel = resendState === 'sent' ? 'sent ✓' : resendState === 'sending' ? 'sending…' : 'resend link';
  const resendShortLabel = resendState === 'sent' ? 'sent ✓' : resendState === 'sending' ? 'sending…' : 'resend';
  const notice: BannerNotice = {
    words: message,
    shortWords: shortMessage,
    action: {
      label: resendLabel,
      shortLabel: resendShortLabel,
      onPress: handleResend,
      disabled: resendState !== 'idle',
    },
  };

  const descriptor: BannerDescriptor | null = eligible
    ? {
        id: 'email-verify',
        tone: 'warn',
        dismissible: true,
        onDismiss: handleDismiss,
        notice,
        shortContent: (
          <>
            <span>{shortMessage}</span>
            <ResendButton type="button" onClick={handleResend} disabled={resendState !== 'idle'}>
              {resendShortLabel}
            </ResendButton>
          </>
        ),
        content: (
          <>
            <span>{message}</span>
            <ResendButton type="button" onClick={handleResend} disabled={resendState !== 'idle'}>
              {resendLabel}
            </ResendButton>
          </>
        ),
      }
    : null;

  useRegisterBanner(bus ? descriptor : null);

  // This source is banner-center-native (no legacy standalone strip existed);
  // outside the center it renders nothing.
  return null;
};

export default EmailVerifyBanner;
